// ============================================================================
// CombatSimulatorTab.jsx - Combat Simulator UI Component
// ============================================================================
// ใช้กับ MetaLab.jsx
// ============================================================================

import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Sword,
  Shield,
  Heart,
  Zap,
  Play,
  Pause,
  RotateCcw,
  ChevronRight,
  Target,
  Clock,
  TrendingUp,
  Activity,
  Skull,
  Award,
  List,
  Sparkles,
} from "lucide-react";

// Import Combat Engine
import {
  createHeroState,
  createGameState,
  runSimulation,
  buildActionQueue,
  getSimulationStats,
  processTick,
  ActionType,
  DamageType,
  TICK_MS,
  TICKS_PER_SECOND,
} from "./CombatEngine";

import { attachPassivesToItems } from "./ItemPassives";
import { CHALLENGER_SPELLS } from "./SpellPassives";
import DamagePreviewPanel from "./DamagePreviewPanel";
import { calculateDamage } from "./CombatEngine";
import { inferSkillEffectsFromDescription } from "./ExcelTextParsers";

// ============================================================================
// ARCANA HELPERS
// ============================================================================

const ARCANA_COLORS = {
  Red: { label: "Red (ATK)", color: "#ef4444", icon: Sword },
  Purple: { label: "Purple (HP)", color: "#a855f7", icon: Zap },
  Green: { label: "Green (DEF)", color: "#22c55e", icon: Shield },
};

const filterArcana = (arcanas, color) =>
  (arcanas || []).filter(a =>
    a.type === "ARCANA" &&
    String(a.color).toLowerCase() === color.toLowerCase() &&
    a.tier === 3
  );

const sumArcanaStats = (arcanaSet, qty = 10) => {
  const out = {};
  for (const key of ["red", "purple", "green"]) {
    const arcana = arcanaSet?.[key];
    if (!arcana?.stats) continue;
    for (const [k, v] of Object.entries(arcana.stats)) {
      out[k] = (out[k] || 0) + (+v || 0) * qty;
    }
  }
  return out;
};

// ============================================================================
// STYLED COMPONENTS (Inline styles matching MetaLab theme)
// ============================================================================

const Card = ({ children, neon = false, style = {} }) => (
  <div
    style={{
      background: "rgba(0, 0, 0, 0.9)",
      backdropFilter: "blur(20px)",
      borderRadius: "16px",
      border: neon ? "2px solid #ff1026" : "1px solid rgba(255,16,38,0.2)",
      boxShadow: neon ? "0 0 30px rgba(255,16,38,0.4), inset 0 0 30px rgba(255,16,38,0.1)" : "none",
      position: "relative",
      transition: "all 0.3s",
      ...style,
    }}
  >
    {children}
  </div>
);

const Button = ({ children, onClick, variant = "primary", style = {}, disabled = false }) => {
  const [hover, setHover] = useState(false);

  const baseStyle = {
    padding: "12px 24px",
    borderRadius: "12px",
    border: "none",
    fontWeight: "bold",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.3s",
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    justifyContent: "center",
    ...style,
  };

  const variants = {
    primary: {
      background: hover && !disabled ? "linear-gradient(135deg, #ff4d5f, #ff1026)" : "linear-gradient(135deg, #ff1026, #a30013)",
      color: "#ffffff",
      boxShadow: hover && !disabled ? "0 0 30px rgba(255,16,38,0.6)" : "0 0 20px rgba(255,16,38,0.4)",
      opacity: disabled ? 0.5 : 1,
    },
    secondary: {
      background: hover && !disabled ? "rgba(0, 0, 0, 0.4)" : "rgba(0, 0, 0, 0.6)",
      color: hover && !disabled ? "#ffffff" : "#9ca3af",
      border: hover && !disabled ? "2px solid #ff1026" : "2px solid rgba(255,16,38,0.3)",
      opacity: disabled ? 0.5 : 1,
    },
    success: {
      background: hover && !disabled ? "linear-gradient(135deg, #22c55e, #16a34a)" : "linear-gradient(135deg, #16a34a, #15803d)",
      color: "#ffffff",
      boxShadow: hover && !disabled ? "0 0 30px rgba(34, 197, 94, 0.6)" : "0 0 20px rgba(34, 197, 94, 0.4)",
      opacity: disabled ? 0.5 : 1,
    },
    danger: {
      background: hover && !disabled ? "linear-gradient(135deg, #ef4444, #dc2626)" : "linear-gradient(135deg, #dc2626, #b91c1c)",
      color: "#ffffff",
      boxShadow: hover && !disabled ? "0 0 30px rgba(239, 68, 68, 0.6)" : "0 0 20px rgba(239, 68, 68, 0.4)",
      opacity: disabled ? 0.5 : 1,
    },
  };

  return (
    <button
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...baseStyle, ...(variants[variant] || variants.primary) }}
    >
      {children}
    </button>
  );
};

// ============================================================================
// HP BAR COMPONENT
// ============================================================================

const HPBar = ({ current, max, label, color = "#ef4444", showText = true }) => {
  const pct = Math.max(0, Math.min(100, (current / max) * 100));

  return (
    <div style={{ width: "100%" }}>
      {showText && (
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 12 }}>
          <span style={{ color: "#9ca3af" }}>{label}</span>
          <span style={{ color: "#fff", fontFamily: "monospace" }}>
            {Math.round(current).toLocaleString()} / {Math.round(max).toLocaleString()}
          </span>
        </div>
      )}
      <div
        style={{
          width: "100%",
          height: 20,
          borderRadius: 10,
          overflow: "hidden",
          background: "rgba(0,0,0,0.6)",
          border: "1px solid rgba(255,16,38,0.2)",
          position: "relative",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            borderRadius: 10,
            background: `linear-gradient(90deg, ${color}, ${color}cc)`,
            boxShadow: `0 0 15px ${color}80`,
            transition: "width 0.3s ease-out",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            fontSize: 11,
            fontWeight: "bold",
            color: "#fff",
            textShadow: "0 0 4px #000",
          }}
        >
          {pct.toFixed(1)}%
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMBO BUILDER COMPONENT
// ============================================================================

const ComboBuilder = ({ combo, setCombo, skills = [], heroName }) => {
  const actions = ["AA", "S1", "S2", "ULT", "SPELL"];

  const addAction = (action) => {
    if (combo.length < 20) {
      setCombo([...combo, action]);
    }
  };

  const removeAction = (idx) => {
    setCombo(combo.filter((_, i) => i !== idx));
  };

  const clearCombo = () => setCombo([]);

  const getActionColor = (action) => {
    switch (action) {
      case "AA": return "#f59e0b";
      case "S1": return "#3b82f6";
      case "S2": return "#8b5cf6";
      case "ULT": return "#ef4444";
      case "SPELL": return "#10b981"; // Emerald Green for Spells
      default: return "#6b7280";
    }
  };

  const getSkillName = (action) => {
    if (action === "AA") return "Auto Attack";
    if (action === "SPELL") return "Challenger Spell";
    const slotMap = { S1: "Skill 1", S2: "Skill 2", ULT: "Ultimate" };
    const skill = skills.find(s => s.slot === slotMap[action]);
    return skill?.name || action;
  };

  return (
    <Card style={{ padding: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontWeight: "bold", color: "#ffd7dc" }}>
          {heroName} Combo ({combo.length}/20)
        </div>
        <Button variant="secondary" onClick={clearCombo} style={{ padding: "6px 12px", fontSize: 12 }}>
          Clear
        </Button>
      </div>

      {/* Action Buttons */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {actions.map((action) => (
          <Button
            key={action}
            variant="secondary"
            onClick={() => addAction(action)}
            disabled={combo.length >= 20}
            style={{
              padding: "8px 16px",
              borderColor: getActionColor(action),
              color: getActionColor(action),
            }}
          >
            + {action}
          </Button>
        ))}
      </div>

      {/* Combo Display */}
      <div
        style={{
          display: "flex",
          gap: 6,
          flexWrap: "wrap",
          minHeight: 40,
          padding: 10,
          background: "rgba(0,0,0,0.4)",
          borderRadius: 8,
          border: "1px solid rgba(255,16,38,0.2)",
        }}
      >
        {combo.length === 0 ? (
          <div style={{ color: "#6b7280", fontSize: 13 }}>คลิกปุ่มด้านบนเพื่อเพิ่ม Action</div>
        ) : (
          combo.map((action, idx) => (
            <div
              key={idx}
              onClick={() => removeAction(idx)}
              style={{
                padding: "4px 10px",
                borderRadius: 6,
                background: getActionColor(action),
                color: "#fff",
                fontSize: 12,
                fontWeight: "bold",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
              title={`${getSkillName(action)} - คลิกเพื่อลบ`}
            >
              {action}
              {idx < combo.length - 1 && (
                <ChevronRight size={12} style={{ opacity: 0.5 }} />
              )}
            </div>
          ))
        )}
      </div>
    </Card>
  );
};

// ============================================================================
// COMBAT LOG COMPONENT
// ============================================================================

const CombatLog = ({ events = [], maxHeight = 300 }) => {
  const logRef = useRef(null);

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [events.length]);

  const getEventIcon = (type) => {
    switch (type) {
      case "AUTO_ATTACK": return <Sword size={12} color="#f59e0b" />;
      case "SKILL_USE": return <Zap size={12} color="#8b5cf6" />;
      case "DAMAGE_DEALT": return <Target size={12} color="#ef4444" />;
      case "HEALING": return <Heart size={12} color="#22c55e" />;
      case "CC_APPLIED": return <Shield size={12} color="#3b82f6" />;
      case "HERO_PASSIVE": return <Activity size={12} color="#ff1026" />;
      case "ITEM_PROC": return <Zap size={12} color="#fbbf24" />;
      case "DEATH": return <Skull size={12} color="#ef4444" />;
      default: return <Activity size={12} color="#6b7280" />;
    }
  };

  const getEventColor = (type) => {
    switch (type) {
      case "AUTO_ATTACK": return "#f59e0b";
      case "SKILL_USE": return "#8b5cf6";
      case "DAMAGE_DEALT": return "#ef4444";
      case "HEALING": return "#22c55e";
      case "CC_APPLIED": return "#3b82f6";
      case "HERO_PASSIVE": return "#ff1026";
      case "ITEM_PROC": return "#fbbf24";
      case "DEATH": return "#ef4444";
      default: return "#9ca3af";
    }
  };

  const formatEvent = (e) => {
    switch (e.type) {
      case "AUTO_ATTACK":
        return `${e.hero} → ${e.target}: ${e.damage} DMG ${e.isCrit ? "💥CRIT" : ""}`;
      case "SKILL_USE":
        return `${e.hero} ใช้ ${e.skill} → ${e.totalDamage} DMG`;
      case "DAMAGE_DEALT":
        return `${e.source} → ${e.target}: ${e.damage} ${e.damageType}`;
      case "HEALING":
        return `${e.hero} +${e.amount} HP (${e.source})`;
      case "CC_APPLIED":
        return `${e.target} โดน ${e.ccType} ${e.duration}s`;
      case "HERO_PASSIVE":
        return `[${e.hero}] ${e.effect}: ${e.detail}`;
      case "ITEM_PROC":
        return `[${e.hero}] ${e.item}: ${e.effect}`;
      case "DEATH":
        return `💀 ${e.hero} ถูกสังหารโดย ${e.killer}`;
      case "RESURRECT":
        return `✨ ${e.hero} คืนชีพ! (${e.source})`;
      default:
        return JSON.stringify(e);
    }
  };

  return (
    <Card style={{ padding: 14 }}>
      <div style={{ fontWeight: "bold", color: "#ffd7dc", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
        <List size={16} />
        Combat Log ({events.length} events)
      </div>
      <div
        ref={logRef}
        className="scroll-container"
        style={{
          maxHeight,
          overflow: "auto",
          background: "rgba(0,0,0,0.4)",
          borderRadius: 8,
          padding: 10,
          fontSize: 12,
          fontFamily: "monospace",
        }}
      >
        {events.length === 0 ? (
          <div style={{ color: "#6b7280" }}>รอการจำลอง...</div>
        ) : (
          events.map((e, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
                padding: "4px 0",
                borderBottom: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              <span style={{ color: "#6b7280", minWidth: 45 }}>[{e.time}s]</span>
              {getEventIcon(e.type)}
              <span style={{ color: getEventColor(e.type) }}>{formatEvent(e)}</span>
            </div>
          ))
        )}
      </div>
    </Card>
  );
};

// ============================================================================
// ARCANA PICKER COMPACT
// ============================================================================

const ArcanaPickerCompact = ({ arcanas, arcanaSet, setArcanaSet, heroName, accentColor = "#ff1026" }) => {
  return (
    <Card style={{ padding: 12 }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 10
      }}>
        <div style={{ fontWeight: "bold", color: accentColor, display: "flex", alignItems: "center", gap: 6 }}>
          <Sparkles size={14} />
          {heroName} Arcana (x10)
        </div>
        <button
          onClick={() => setArcanaSet({ red: null, purple: null, green: null })}
          style={{
            padding: "4px 8px",
            borderRadius: 6,
            border: "1px solid rgba(255,16,38,0.3)",
            background: "rgba(0,0,0,0.4)",
            color: "#9ca3af",
            fontSize: 10,
            cursor: "pointer",
          }}
        >
          Clear
        </button>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {Object.entries(ARCANA_COLORS).map(([colorKey, config]) => {
          const list = filterArcana(arcanas, colorKey);
          const current = arcanaSet[colorKey.toLowerCase()];
          const Icon = config.icon;

          return (
            <div key={colorKey} style={{ flex: 1, minWidth: 120 }}>
              <div style={{
                color: config.color,
                fontWeight: "bold",
                marginBottom: 4,
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 10
              }}>
                <Icon size={10} /> {config.label}
              </div>
              <select
                value={current?.name || ""}
                onChange={(e) => {
                  const val = list.find((x) => x.name === e.target.value) || null;
                  setArcanaSet((prev) => ({ ...prev, [colorKey.toLowerCase()]: val }));
                }}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  background: "rgba(0,0,0,0.6)",
                  border: `1px solid ${config.color}40`,
                  borderRadius: 6,
                  color: "#fff",
                  fontSize: 11,
                  fontFamily: "'Rajdhani', sans-serif",
                }}
              >
                <option value="">- None -</option>
                {list.map((a) => (
                  <option key={a.name} value={a.name}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
      {/* Show selected stats summary */}
      {(arcanaSet.red || arcanaSet.purple || arcanaSet.green) && (
        <div style={{
          marginTop: 8,
          padding: 6,
          background: "rgba(255,16,38,0.1)",
          borderRadius: 6,
          fontSize: 10,
          color: "#9ca3af"
        }}>
          {Object.entries(sumArcanaStats(arcanaSet, 10))
            .filter(([_, v]) => v !== 0)
            .slice(0, 4)
            .map(([k, v]) => `${k}: +${Math.round(v)}`)
            .join(" • ")}
        </div>
      )}
    </Card>
  );
};

// ============================================================================
// SPELL PICKER COMPACT
// ============================================================================

const SpellPickerCompact = ({ selected, onChange, heroName, accentColor = "#ff1026" }) => {
  return (
    <Card style={{ padding: 12 }}>
      <div style={{
        fontWeight: "bold",
        color: accentColor,
        marginBottom: 8,
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: 12
      }}>
        <Zap size={14} />
        {heroName} Spell
      </div>
      <select
        value={selected || ""}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          padding: "8px 10px",
          background: "rgba(0,0,0,0.6)",
          border: `1px solid ${accentColor}40`,
          borderRadius: 8,
          color: "#fff",
          fontSize: 12,
          fontFamily: "'Rajdhani', sans-serif",
        }}
      >
        {Object.values(CHALLENGER_SPELLS).map((spell) => (
          <option key={spell.name} value={spell.name}>
            {spell.name} ({spell.cooldown}s)
          </option>
        ))}
      </select>
      {selected && CHALLENGER_SPELLS[selected] && (
        <div style={{ marginTop: 6, fontSize: 10, color: "#9ca3af", lineHeight: "1.2" }}>
          {CHALLENGER_SPELLS[selected].description}
        </div>
      )}
    </Card>
  );
};

// ============================================================================
// STATISTICS PANEL
// ============================================================================

const StatisticsPanel = ({ stats }) => {
  if (!stats) return null;

  const StatRow = ({ label, value, color = "#fff" }) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
      <span style={{ color: "#9ca3af" }}>{label}</span>
      <span style={{ color, fontFamily: "monospace", fontWeight: "bold" }}>{value}</span>
    </div>
  );

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
      {/* Hero 1 Stats */}
      <Card style={{ padding: 14 }}>
        <div style={{ fontWeight: "bold", color: "#3b82f6", marginBottom: 10 }}>
          {stats.hero1.name}
        </div>
        <StatRow label="HP Remaining" value={`${stats.hero1.hpPct}%`} color={stats.hero1.hpPct > 50 ? "#22c55e" : "#ef4444"} />
        <StatRow label="Total Damage" value={Math.round(stats.hero1.totalDamageDealt).toLocaleString()} color="#ef4444" />
        <StatRow label="DPS" value={Math.round(stats.hero1.dps).toLocaleString()} color="#f59e0b" />
        <StatRow label="Total Healing" value={Math.round(stats.hero1.totalHealing).toLocaleString()} color="#22c55e" />
        <StatRow label="Skills Used" value={stats.hero1.skillsUsed} />
        <StatRow label="Auto Attacks" value={stats.hero1.aaCasts} />
        <StatRow label="Crit Rate" value={`${stats.hero1.critRate}%`} />
      </Card>

      {/* Hero 2 Stats */}
      <Card style={{ padding: 14 }}>
        <div style={{ fontWeight: "bold", color: "#ef4444", marginBottom: 10 }}>
          {stats.hero2.name}
        </div>
        <StatRow label="HP Remaining" value={`${stats.hero2.hpPct}%`} color={stats.hero2.hpPct > 50 ? "#22c55e" : "#ef4444"} />
        <StatRow label="Total Damage" value={Math.round(stats.hero2.totalDamageDealt).toLocaleString()} color="#ef4444" />
        <StatRow label="DPS" value={Math.round(stats.hero2.dps).toLocaleString()} color="#f59e0b" />
        <StatRow label="Total Healing" value={Math.round(stats.hero2.totalHealing).toLocaleString()} color="#22c55e" />
        <StatRow label="Skills Used" value={stats.hero2.skillsUsed} />
        <StatRow label="Auto Attacks" value={stats.hero2.aaCasts} />
        <StatRow label="Crit Rate" value={`${stats.hero2.critRate}%`} />
      </Card>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT: CombatSimulatorTab
// ============================================================================

export default function CombatSimulatorTab({
  patch,
  getHeroImageUrl,
  HeroSelector,
  ItemShopPicker,
  LevelSelect
}) {
  // Heroes & Items from patch
  const heroes = useMemo(() => [...(patch?.heroes || [])].sort((a, b) => a.name.localeCompare(b.name)), [patch]);
  const items = useMemo(() => patch?.items || [], [patch]);
  const skills = useMemo(() => patch?.skills || [], [patch]);
  const arcanas = useMemo(() => patch?.runes || [], [patch]);

  // Hero Selection
  const [hero1Name, setHero1Name] = useState(heroes[0]?.name || "");
  const [hero2Name, setHero2Name] = useState(heroes[1]?.name || heroes[0]?.name || "");
  const [hero1Level, setHero1Level] = useState(15);
  const [hero2Level, setHero2Level] = useState(15);

  // Items
  const [hero1Items, setHero1Items] = useState([]);
  const [hero2Items, setHero2Items] = useState([]);

  // Arcana
  const [hero1Arcana, setHero1Arcana] = useState({ red: null, purple: null, green: null });
  const [hero2Arcana, setHero2Arcana] = useState({ red: null, purple: null, green: null });

  // Spells
  const [hero1Spell, setHero1Spell] = useState("Execute");
  const [hero2Spell, setHero2Spell] = useState("Execute");

  // Combos
  const [hero1Combo, setHero1Combo] = useState(["AA", "S1", "AA", "S2", "AA", "ULT", "AA", "AA"]);
  const [hero2Combo, setHero2Combo] = useState(["AA", "S1", "AA", "S2", "AA", "ULT", "AA", "AA"]);

  // Simulation State
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isManualMode, setIsManualMode] = useState(false); // Manual Mode state
  const [gameState, setGameState] = useState(null);
  const [simStats, setSimStats] = useState(null);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  const intervalRef = useRef(null);

  // Get hero data
  const hero1Data = useMemo(() => heroes.find(h => h.name === hero1Name), [heroes, hero1Name]);
  const hero2Data = useMemo(() => heroes.find(h => h.name === hero2Name), [heroes, hero2Name]);

  const hero1Skills = useMemo(() => skills.filter(s => s.hero === hero1Name), [skills, hero1Name]);
  const hero2Skills = useMemo(() => skills.filter(s => s.hero === hero2Name), [skills, hero2Name]);

  const getSkillRankByLevel = (heroLevel, slot) => {
    const lv = Math.max(1, Math.min(15, +heroLevel || 1));
    if (slot === "Ultimate") {
      if (lv >= 12) return 3;
      if (lv >= 8) return 2;
      if (lv >= 4) return 1;
      return 0;
    }

    // Typical MOBA curve for two normal skills: 6 ranks by level 15.
    const pointsSpent = Math.max(1, lv - (lv >= 4 ? 1 : 0) - (lv >= 8 ? 1 : 0) - (lv >= 12 ? 1 : 0));
    const s1 = Math.min(6, Math.ceil(pointsSpent / 2));
    const s2 = Math.min(6, Math.floor(pointsSpent / 2));
    return slot === "Skill 1" ? s1 : s2;
  };

  const getCooldownAtRank = (cooldownArr, rank, slot) => {
    const arr = Array.isArray(cooldownArr) ? cooldownArr.filter((x) => Number.isFinite(+x) && +x > 0).map(Number) : [];
    if (!arr.length) return slot === "Ultimate" ? 40 : 8;
    if (arr.length === 1) return arr[0];

    // Data can be [max,min] or [min,max] from simplified parse.
    const hi = Math.max(...arr);
    const lo = Math.min(...arr);
    const maxRank = slot === "Ultimate" ? 3 : 6;
    const t = maxRank <= 1 ? 0 : (Math.max(1, Math.min(maxRank, rank)) - 1) / (maxRank - 1);
    return hi + (lo - hi) * t;
  };

  const getBaseDamageAtRank = (baseDmgArr, rank, slot) => {
    const arr = Array.isArray(baseDmgArr) ? baseDmgArr.filter((x) => Number.isFinite(+x)).map(Number) : [];
    if (!arr.length) return 0;
    const maxRank = slot === "Ultimate" ? 3 : Math.min(6, arr.length);
    const idx = Math.max(0, Math.min(arr.length - 1, Math.min(rank, maxRank) - 1));
    return arr[idx] || arr[arr.length - 1] || 0;
  };

  const getManaCostAtRank = (manaArr, rank, slot) => {
    const arr = Array.isArray(manaArr) ? manaArr.filter((x) => Number.isFinite(+x)).map(Number) : [];
    if (!arr.length) return 0;
    const maxRank = slot === "Ultimate" ? 3 : Math.min(6, arr.length);
    const idx = Math.max(0, Math.min(arr.length - 1, Math.min(rank, maxRank) - 1));
    return arr[idx] || 0;
  };

  // Helper: Get hero level object
  const getHeroLevelObj = (hero, level) => {
    if (!hero?.levels) return null;
    return hero.levels[level] || hero.levels[1] || Object.values(hero.levels)[0] || null;
  };

  // Convert combo array to action queue
  const comboToActionQueue = (combo, heroSkills, spellName, heroLevel = 15) => {
    return combo.map(action => {
      if (action === "AA") {
        return { type: ActionType.AUTO_ATTACK, name: "Auto Attack", castTime: 0 };
      }

      if (action === "SPELL") {
        return {
          type: ActionType.SPELL,
          spellName: spellName,
          name: spellName,
          castTime: 0
        };
      }

      const slotMap = { S1: "Skill 1", S2: "Skill 2", ULT: "Ultimate" };
      const slotName = slotMap[action];
      const skillData = heroSkills.find(s => s.slot === slotName);
      const inferred = inferSkillEffectsFromDescription(skillData || {});
      const rank = getSkillRankByLevel(heroLevel, slotName);

      if (!skillData || rank <= 0) {
        return { type: ActionType.AUTO_ATTACK, name: "Auto Attack", castTime: 0 };
      }

      return {
        type: ActionType.SKILL,
        name: skillData.name,
        skillSlot: slotName,
        baseDamage: getBaseDamageAtRank(skillData.base_dmg, rank, slotName),
        adScaling: skillData.ad_scaling || 0,
        apScaling: skillData.ap_scaling || 0,
        damageType: skillData.damage_type?.includes("True")
          ? DamageType.TRUE
          : skillData.damage_type?.includes("Magic")
            ? DamageType.MAGIC
            : inferred.damageType || DamageType.PHYSICAL,
        cooldown: getCooldownAtRank(skillData.cooldown, rank, slotName),
        manaCost: getManaCostAtRank(skillData.mana_cost, rank, slotName),
        castTime: 0.3,
        damageInstances: skillData.damage_instances || inferred.damageInstances || 1,
        canCrit: false,
        ccType: skillData.cc_type || inferred.ccType || null,
        ccDuration: skillData.cc_duration || inferred.ccDuration || ((skillData.cc_type || inferred.ccType) ? 1 : 0),
        grantsBuff: skillData.grants_buff || inferred.grantsBuff || null,
        isUntargetable: skillData.is_untargetable || inferred.isUntargetable || false,
        untargetableDuration: skillData.untargetable_duration || inferred.untargetableDuration || 0,
        hpMaxScaling: skillData.hp_max_scaling || inferred.hpMaxScaling || 0,
        hpCurrentScaling: skillData.hp_current_scaling || inferred.targetCurrentHpScaling || 0,
        targetMaxScaling: inferred.targetMaxHpScaling || 0,
        hpLostScaling: skillData.hp_lost_scaling || inferred.targetLostHpScaling || 0,
        executeThresholdPct: inferred.executeThresholdPct || 0,
      };
    });
  };

  // Helper: Create Hero State (Refactored for reuse in preview)
  const computeHeroState = (heroData, level, items, arcanaSet) => {
    if (!heroData) return null;
    const levelObj = getHeroLevelObj(heroData, level);
    if (!levelObj) return null;

    const itemsWithPassives = attachPassivesToItems(items);
    const arcanaStats = sumArcanaStats(arcanaSet, 10);

    const skillRanks = {
      "Skill 1": getSkillRankByLevel(level, "Skill 1"),
      "Skill 2": getSkillRankByLevel(level, "Skill 2"),
      "Ultimate": getSkillRankByLevel(level, "Ultimate"),
    };

    const state = createHeroState(
      { ...heroData, ...levelObj, hero_name: heroData.name },
      level,
      itemsWithPassives,
      [],
      skillRanks
    );

    // Apply ALWAYS item passive stat modifiers from attached passives.
    const prevHP = state.totalStats.hp || state.maxHP || 0;
    const prevMana = state.totalStats.max_mana || state.maxMana || 0;
    for (const item of itemsWithPassives) {
      if (item?.passive?.applyToStats) {
        state.totalStats = item.passive.applyToStats({ ...state.totalStats });
      }
    }
    const nextHP = state.totalStats.hp || prevHP;
    const nextMana = state.totalStats.max_mana || prevMana;
    const hpDelta = nextHP - prevHP;
    const manaDelta = nextMana - prevMana;
    if (hpDelta !== 0) {
      state.maxHP += hpDelta;
      state.currentHP += hpDelta;
    }
    if (manaDelta !== 0) {
      state.maxMana += manaDelta;
      state.currentMana += manaDelta;
    }

    // Apply arcana
    for (const [k, v] of Object.entries(arcanaStats)) {
      if (state.totalStats[k] !== undefined) state.totalStats[k] += v;
      if (k === 'hp') { state.currentHP += v; state.maxHP += v; }
    }
    return state;
  };

  // Preview Logic
  const damagePreview1 = useMemo(() => {
    if (!hero1Data || !hero2Data) return [];

    // Create dummy states for calculation
    const attacker = computeHeroState(hero1Data, hero1Level, hero1Items, hero1Arcana);
    const defender = computeHeroState(hero2Data, hero2Level, hero2Items, hero2Arcana);

    if (!attacker || !defender) return [];

    const previews = [];

    // 1. Auto Attack
    const aaBreakdown = calculateDamage(attacker, defender, {
      name: 'Auto Attack',
      baseDamage: 0,
      adScaling: 1.0,
      damageType: DamageType.PHYSICAL,
      canCrit: true // Preview assumes normal hit usually, or avg? Let's show normal hit.
    });
    previews.push({
      skillName: "Auto Attack",
      rawDamage: Math.round(attacker.totalStats.phys_atk), // Approx raw
      netDamage: aaBreakdown.finalDamage,
      damageType: "Physical",
      reductionPct: Math.round((1 - (aaBreakdown.finalDamage / Math.max(1, attacker.totalStats.phys_atk))) * 100),
      hpCutPct: ((aaBreakdown.finalDamage / defender.maxHP) * 100).toFixed(1),
      color: "#f59e0b"
    });

    // 2. Skills
    hero1Skills.forEach(skill => {
      // Simple logic to find base damage at max level (or current)
      // Skill data structure from excel...
      const base = skill.base_dmg?.[5] || skill.base_dmg?.[0] || 0;
      const adScale = skill.ad_scaling || 0;
      const apScale = skill.ap_scaling || 0;
      const type = skill.damage_type?.includes("Magic") ? DamageType.MAGIC : DamageType.PHYSICAL;

      // Calculate Raw manually for display
      const raw = base + (attacker.totalStats.phys_atk * adScale) + (attacker.totalStats.magic_power * apScale);

      const breakdown = calculateDamage(attacker, defender, {
        name: skill.name,
        baseDamage: base,
        adScaling: adScale,
        apScaling: apScale,
        damageType: type,
        canCrit: false
      });

      previews.push({
        skillName: skill.name,
        rawDamage: Math.round(raw),
        netDamage: breakdown.finalDamage,
        damageType: type === DamageType.MAGIC ? "Magic" : "Physical",
        reductionPct: Math.round((1 - (breakdown.finalDamage / Math.max(1, raw))) * 100),
        hpCutPct: ((breakdown.finalDamage / defender.maxHP) * 100).toFixed(1),
        color: type === DamageType.MAGIC ? "#8b5cf6" : "#3b82f6"
      });
    });

    // 3. Spell (Execute)
    if (hero1Spell === "Execute") {
      // Execute deals 16% of LOST HP. At start, lost HP is 0. 
      // So display "Max Damage" potential (e.g. if target at 1 HP?) 
      // Or display based on "Target at 50%"?
      // Let's display "Max Potential" (Target at 0 HP essentially)
      const maxExec = defender.maxHP * 0.16;
      previews.push({
        skillName: "Execute (Max)",
        rawDamage: Math.round(maxExec),
        netDamage: Math.round(maxExec),
        damageType: "True",
        reductionPct: 0,
        hpCutPct: 16.0,
        color: "#fff"
      });
    }

    return previews;
  }, [hero1Data, hero2Data, hero1Level, hero2Level, hero1Items, hero2Items, hero1Arcana, hero2Arcana, hero1Skills, hero1Spell]);

  // Mirror for Hero 2
  const damagePreview2 = useMemo(() => {
    if (!hero1Data || !hero2Data) return [];

    // Create dummy states for calculation (Hero 2 attacking Hero 1)
    const attacker = computeHeroState(hero2Data, hero2Level, hero2Items, hero2Arcana);
    const defender = computeHeroState(hero1Data, hero1Level, hero1Items, hero1Arcana);

    if (!attacker || !defender) return [];

    const previews = [];

    // 1. Auto Attack
    const aaBreakdown = calculateDamage(attacker, defender, {
      name: 'Auto Attack',
      baseDamage: 0,
      adScaling: 1.0,
      damageType: DamageType.PHYSICAL,
      canCrit: true
    });
    previews.push({
      skillName: "Auto Attack",
      rawDamage: Math.round(attacker.totalStats.phys_atk),
      netDamage: aaBreakdown.finalDamage,
      damageType: "Physical",
      reductionPct: Math.round((1 - (aaBreakdown.finalDamage / Math.max(1, attacker.totalStats.phys_atk))) * 100),
      hpCutPct: ((aaBreakdown.finalDamage / defender.maxHP) * 100).toFixed(1),
      color: "#f59e0b"
    });

    // 2. Skills
    hero2Skills.forEach(skill => {
      const base = skill.base_dmg?.[5] || skill.base_dmg?.[0] || 0;
      const adScale = skill.ad_scaling || 0;
      const apScale = skill.ap_scaling || 0;
      const type = skill.damage_type?.includes("Magic") ? DamageType.MAGIC : DamageType.PHYSICAL;

      const raw = base + (attacker.totalStats.phys_atk * adScale) + (attacker.totalStats.magic_power * apScale);

      const breakdown = calculateDamage(attacker, defender, {
        name: skill.name,
        baseDamage: base,
        adScaling: adScale,
        apScaling: apScale,
        damageType: type,
        canCrit: false
      });

      previews.push({
        skillName: skill.name,
        rawDamage: Math.round(raw),
        netDamage: breakdown.finalDamage,
        damageType: type === DamageType.MAGIC ? "Magic" : "Physical",
        reductionPct: Math.round((1 - (breakdown.finalDamage / Math.max(1, raw))) * 100),
        hpCutPct: ((breakdown.finalDamage / defender.maxHP) * 100).toFixed(1),
        color: type === DamageType.MAGIC ? "#8b5cf6" : "#ef4444"
      });
    });

    return previews;
  }, [hero1Data, hero2Data, hero1Level, hero2Level, hero1Items, hero2Items, hero1Arcana, hero2Arcana, hero2Skills, hero2Spell]);


  // Start Simulation
  const startSimulation = () => {
    if (!hero1Data || !hero2Data) {
      alert("กรุณาเลือก Hero ทั้ง 2 ฝ่าย");
      return;
    }

    const hero1State = computeHeroState(hero1Data, hero1Level, hero1Items, hero1Arcana);
    const hero2State = computeHeroState(hero2Data, hero2Level, hero2Items, hero2Arcana);

    if (!hero1State || !hero2State) {
      alert("Error creating hero states");
      return;
    }

    // Build action queues
    hero1State.actionQueue = comboToActionQueue(hero1Combo, hero1Skills, hero1Spell, hero1Level);
    hero2State.actionQueue = comboToActionQueue(hero2Combo, hero2Skills, hero2Spell, hero2Level);

    // Create game state
    const newGameState = createGameState(hero1State, hero2State);
    setGameState(newGameState);
    setSimStats(null);
    setIsManualMode(false);
    setIsRunning(true);
    setIsPaused(false);
  };

  // Run simulation tick by tick
  useEffect(() => {
    if (!isRunning || isPaused || !gameState) return;

    intervalRef.current = setInterval(() => {
      setGameState(prev => {
        if (!prev || prev.isFinished) {
          setIsRunning(false);
          if (prev) {
            setSimStats(getSimulationStats(prev));
          }
          return prev;
        }

        const newState = { ...prev };
        processTick(newState);

        // Refill action queues if empty (loop combo)
        if (newState.hero1.actionQueue.length === 0 && newState.hero1.isAlive) {
          newState.hero1.actionQueue = comboToActionQueue(hero1Combo, hero1Skills, hero1Spell, hero1Level);
        }
        if (newState.hero2.actionQueue.length === 0 && newState.hero2.isAlive) {
          newState.hero2.actionQueue = comboToActionQueue(hero2Combo, hero2Skills, hero2Spell, hero2Level);
        }

        // Auto-stop at 30 seconds
        if (newState.timeSeconds >= 30) {
          newState.isFinished = true;
          const hp1Pct = newState.hero1.currentHP / newState.hero1.maxHP;
          const hp2Pct = newState.hero2.currentHP / newState.hero2.maxHP;
          newState.winner = hp1Pct >= hp2Pct ? newState.hero1.heroName : newState.hero2.heroName;
        }

        return newState;
      });
    }, TICK_MS / playbackSpeed);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning, isPaused, playbackSpeed, hero1Combo, hero2Combo, hero1Skills, hero2Skills, hero1Spell, hero2Spell, hero1Level, hero2Level]);

  // Stop when finished
  useEffect(() => {
    if (gameState?.isFinished && isRunning) {
      setIsRunning(false);
      setSimStats(getSimulationStats(gameState));
    }
  }, [gameState?.isFinished]);

  // Reset
  const resetSimulation = () => {
    setIsRunning(false);
    setIsPaused(false);
    setIsManualMode(false);
    setGameState(null);
    setSimStats(null);
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  // Run Instant (no animation)
  const runInstant = () => {
    if (!hero1Data || !hero2Data) {
      alert("กรุณาเลือก Hero ทั้ง 2 ฝ่าย");
      return;
    }

    const hero1State = computeHeroState(hero1Data, hero1Level, hero1Items, hero1Arcana);
    const hero2State = computeHeroState(hero2Data, hero2Level, hero2Items, hero2Arcana);

    // Build larger action queue for instant run
    const repeatCombo = (combo, times) => Array(times).fill(combo).flat();
    hero1State.actionQueue = comboToActionQueue(repeatCombo(hero1Combo, 10), hero1Skills, hero1Spell, hero1Level);
    hero2State.actionQueue = comboToActionQueue(repeatCombo(hero2Combo, 10), hero2Skills, hero2Spell, hero2Level);

    const newGameState = createGameState(hero1State, hero2State);
    const result = runSimulation(newGameState, 30);

    setGameState(result);
    setSimStats(getSimulationStats(result));
    setIsManualMode(false);
    setIsRunning(false);
    setIsPaused(false);
  };

  // ===================================
  // MANUAL CONTROL LOGIC
  // ===================================

  const startManualSession = () => {
    if (!hero1Data || !hero2Data) return;

    const hero1State = computeHeroState(hero1Data, hero1Level, hero1Items, hero1Arcana);
    const hero2State = computeHeroState(hero2Data, hero2Level, hero2Items, hero2Arcana);

    if (!hero1State || !hero2State) return;

    // No action queue for manual mode
    hero1State.actionQueue = [];
    hero2State.actionQueue = [];

    const newGameState = createGameState(hero1State, hero2State);
    setGameState(newGameState);
    setSimStats(getSimulationStats(newGameState));
    setIsRunning(false);
    setIsManualMode(true);
  };

  const triggerManualAction = (heroId, actionType, skillSlotOrSpell) => {
    if (!gameState || gameState.isFinished) return;

    setGameState(prev => {
      const next = { ...prev };
      const actor = heroId === 1 ? next.hero1 : next.hero2;
      const target = heroId === 1 ? next.hero2 : next.hero1;
      const actorLevel = heroId === 1 ? hero1Level : hero2Level;

      // Determine action object
      let actionObj = null;
      if (actionType === "AA") {
        actionObj = { type: ActionType.AUTO_ATTACK, name: "Auto Attack", castTime: 0 };
      } else if (actionType === "SKILL") {
        const skills = heroId === 1 ? hero1Skills : hero2Skills;
        const skillData = skills.find(s => s.slot === skillSlotOrSpell);
        if (skillData) {
          const inferred = inferSkillEffectsFromDescription(skillData || {});
          const rank = getSkillRankByLevel(actorLevel, skillSlotOrSpell);
          if (rank <= 0) {
            return next;
          }
          actionObj = {
            type: ActionType.SKILL,
            name: skillData.name,
            skillSlot: skillSlotOrSpell,
            baseDamage: getBaseDamageAtRank(skillData.base_dmg, rank, skillSlotOrSpell),
            adScaling: skillData.ad_scaling || 0,
            apScaling: skillData.ap_scaling || 0,
            damageType: skillData.damage_type?.includes("True")
              ? DamageType.TRUE
              : skillData.damage_type?.includes("Magic")
                ? DamageType.MAGIC
                : inferred.damageType || DamageType.PHYSICAL,
            cooldown: getCooldownAtRank(skillData.cooldown, rank, skillSlotOrSpell),
            manaCost: getManaCostAtRank(skillData.mana_cost, rank, skillSlotOrSpell),
            castTime: 0.3,
            damageInstances: skillData.damage_instances || inferred.damageInstances || 1,
            canCrit: false,
            ccType: skillData.cc_type || inferred.ccType || null,
            ccDuration: skillData.cc_duration || inferred.ccDuration || ((skillData.cc_type || inferred.ccType) ? 1 : 0),
            grantsBuff: skillData.grants_buff || inferred.grantsBuff || null,
            isUntargetable: skillData.is_untargetable || inferred.isUntargetable || false,
            untargetableDuration: skillData.untargetable_duration || inferred.untargetableDuration || 0,
            hpMaxScaling: skillData.hp_max_scaling || inferred.hpMaxScaling || 0,
            hpCurrentScaling: skillData.hp_current_scaling || inferred.targetCurrentHpScaling || 0,
            targetMaxScaling: inferred.targetMaxHpScaling || 0,
            hpLostScaling: skillData.hp_lost_scaling || inferred.targetLostHpScaling || 0,
            executeThresholdPct: inferred.executeThresholdPct || 0,
          };
        }
      } else if (actionType === "SPELL") {
        actionObj = {
          type: ActionType.SPELL,
          spellName: skillSlotOrSpell,
          name: skillSlotOrSpell,
          castTime: 0
        };
      } else if (actionType === "ITEM_ACTIVE") {
        const item = (actor.items || []).find((it) => (it?.name || "") === skillSlotOrSpell && it?.active);
        if (item) {
          actionObj = {
            type: ActionType.ITEM_ACTIVE,
            name: `[Item] ${item.name}`,
            item,
            castTime: 0,
          };
        }
      }

      if (actionObj) {
        actor.actionQueue.push(actionObj);
      }

      // Process ticks until action completes or slightly advances time
      // Pump 5 ticks (0.5s) or until idle?
      // Better: Process 1 tick at a time until queue empty? 
      // Manual mode usually wants discrete steps. Let's process 3 ticks (0.3s) minimum to ensure cast starts.
      // Actually, let's process until the action queue is empty!

      let safetyBreak = 0;
      while ((actor.actionQueue.length > 0 || target.actionQueue.length > 0 || actor.currentAction || target.currentAction) && safetyBreak < 50) {
        processTick(next);
        safetyBreak++;
      }
      // Process a few more ticks to let animations/numbers conclude (cooldowns tick down)
      processTick(next);
      processTick(next);

      // Check win condition
      if (next.hero1.currentHP <= 0 || next.hero2.currentHP <= 0 || next.timeSeconds >= 120) {
        next.isFinished = true;
        const hp1 = next.hero1.currentHP;
        const hp2 = next.hero2.currentHP;
        next.winner = hp1 > hp2 ? next.hero1.heroName : next.hero2.heroName;
      }

      return next;
    });

    // Update stats immediately
    setTimeout(() => {
      setGameState(current => {
        if (current) setSimStats(getSimulationStats(current));
        return current;
      });
    }, 0);
  };

  return (
    <div style={{ display: "grid", gap: 14 }}>
      {/* Header */}
      <Card neon style={{ padding: 20 }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
          marginBottom: 20
        }}>
          <Sword size={32} color="#ff1026" />
          <h2 style={{
            fontFamily: "'Orbitron', sans-serif",
            fontSize: "1.8rem",
            fontWeight: 900,
            background: "linear-gradient(to right, #ff1026, #f5f5f5)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            letterSpacing: "0.1em"
          }}>
            COMBAT SIMULATOR
          </h2>
          <Shield size={32} color="#ff1026" />
        </div>

        {/* Hero Selection */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          gap: 20,
          alignItems: "center",
          marginBottom: 20
        }}>
          {/* Hero 1 */}
          <div>
            <div style={{ color: "#3b82f6", fontWeight: "bold", marginBottom: 8, fontSize: 18 }}>
              🔵 HERO 1
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {HeroSelector && (
                <HeroSelector
                  heroes={heroes}
                  value={hero1Name}
                  onChange={(e) => setHero1Name(e.target.value)}
                  label="Select Hero"
                  getHeroImageUrl={getHeroImageUrl}
                  showRoleFilter={true}
                  maxHeight={300}
                />
              )}
              <div style={{ minWidth: 120 }}>
                <div style={{ color: "#9ca3af", fontWeight: "bold", marginBottom: 6 }}>Level</div>
                <select
                  value={hero1Level}
                  onChange={(e) => setHero1Level(+e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    background: "rgba(0, 0, 0, 0.6)",
                    border: "2px solid rgba(255,16,38,0.3)",
                    borderRadius: "12px",
                    color: "#ffffff",
                    fontSize: "14px",
                    fontFamily: "'Rajdhani', sans-serif",
                  }}
                >
                  {[...Array(15)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>Lv {i + 1}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* VS */}
          <div style={{
            fontSize: "2.5rem",
            fontWeight: 900,
            color: "#ff1026",
            fontFamily: "'Orbitron', sans-serif",
            textShadow: "0 0 20px rgba(255,16,38,0.8)"
          }}>
            VS
          </div>

          {/* Hero 2 */}
          <div>
            <div style={{ color: "#ef4444", fontWeight: "bold", marginBottom: 8, fontSize: 18 }}>
              🔴 HERO 2
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {HeroSelector && (
                <HeroSelector
                  heroes={heroes}
                  value={hero2Name}
                  onChange={(e) => setHero2Name(e.target.value)}
                  label="Select Hero"
                  getHeroImageUrl={getHeroImageUrl}
                  showRoleFilter={true}
                  maxHeight={300}
                />
              )}
              <div style={{ minWidth: 120 }}>
                <div style={{ color: "#9ca3af", fontWeight: "bold", marginBottom: 6 }}>Level</div>
                <select
                  value={hero2Level}
                  onChange={(e) => setHero2Level(+e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    background: "rgba(0, 0, 0, 0.6)",
                    border: "2px solid rgba(255,16,38,0.3)",
                    borderRadius: "12px",
                    color: "#ffffff",
                    fontSize: "14px",
                    fontFamily: "'Rajdhani', sans-serif",
                  }}
                >
                  {[...Array(15)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>Lv {i + 1}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Control Buttons */}
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <Button
            variant="success"
            onClick={startSimulation}
            disabled={isRunning}
            style={{ minWidth: 160 }}
          >
            <Play size={18} />
            เริ่มจำลอง (Animate)
          </Button>

          <Button
            variant="primary"
            onClick={runInstant}
            disabled={isRunning}
            style={{ minWidth: 160 }}
          >
            <Zap size={18} />
            รันทันที
          </Button>

          {isRunning && (
            <Button
              variant="secondary"
              onClick={() => setIsPaused(!isPaused)}
              style={{ minWidth: 120 }}
            >
              {isPaused ? <Play size={18} /> : <Pause size={18} />}
              {isPaused ? "Resume" : "Pause"}
            </Button>
          )}

          <Button
            variant="danger"
            onClick={resetSimulation}
            style={{ minWidth: 120 }}
          >
            <RotateCcw size={18} />
            Reset
          </Button>

          {/* Speed Control */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "#9ca3af", fontSize: 12 }}>Speed:</span>
            {[1, 2, 4, 8].map(speed => (
              <button
                key={speed}
                onClick={() => setPlaybackSpeed(speed)}
                style={{
                  padding: "6px 12px",
                  borderRadius: 6,
                  border: playbackSpeed === speed ? "2px solid #ff1026" : "1px solid rgba(255,16,38,0.3)",
                  background: playbackSpeed === speed ? "rgba(255,16,38,0.2)" : "rgba(0,0,0,0.4)",
                  color: playbackSpeed === speed ? "#ff1026" : "#9ca3af",
                  cursor: "pointer",
                  fontWeight: "bold",
                  fontSize: 12,
                }}
              >
                {speed}x
              </button>
            ))}
          </div>

          <Button
            variant="secondary"
            onClick={startManualSession}
            style={{ minWidth: 140, border: "2px solid #10b981", color: "#10b981" }}
          >
            <Target size={18} />
            Manual Mode
          </Button>
        </div>
      </Card>

      {/* Manual Control Panel */}
      {isManualMode && gameState && !gameState.isFinished && (
        <Card neon style={{ padding: 16, border: "2px solid #10b981" }}>
          <div style={{ textAlign: "center", marginBottom: 12, color: "#10b981", fontWeight: "bold" }}>
            MANUAL CONTROL ACTIVE - CLICK TO EXECUTE
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            {/* Hero 1 Controls */}
            <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
              <div style={{ width: "100%", textAlign: "center", color: "#3b82f6", fontSize: 12, fontWeight: "bold" }}>{hero1Name} Action</div>
              <Button onClick={() => triggerManualAction(1, "AA")} style={{ background: "#f59e0b", padding: "8px 12px" }}>AA</Button>
              <Button onClick={() => triggerManualAction(1, "SKILL", "Skill 1")} style={{ background: "#3b82f6", padding: "8px 12px" }}>S1</Button>
              <Button onClick={() => triggerManualAction(1, "SKILL", "Skill 2")} style={{ background: "#8b5cf6", padding: "8px 12px" }}>S2</Button>
              <Button onClick={() => triggerManualAction(1, "SKILL", "Ultimate")} style={{ background: "#ef4444", padding: "8px 12px" }}>Ult</Button>
              <Button onClick={() => triggerManualAction(1, "SPELL", hero1Spell)} style={{ background: "#10b981", padding: "8px 12px" }}>Spell</Button>
              {(gameState.hero1?.items || []).filter(it => it?.active).map((it) => (
                <Button
                  key={`h1-${it.name}`}
                  onClick={() => triggerManualAction(1, "ITEM_ACTIVE", it.name)}
                  style={{ background: "#f97316", padding: "8px 12px" }}
                >
                  {it.name}
                </Button>
              ))}
            </div>

            {/* Hero 2 Controls */}
            <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
              <div style={{ width: "100%", textAlign: "center", color: "#ef4444", fontSize: 12, fontWeight: "bold" }}>{hero2Name} Action</div>
              <Button onClick={() => triggerManualAction(2, "AA")} style={{ background: "#f59e0b", padding: "8px 12px" }}>AA</Button>
              <Button onClick={() => triggerManualAction(2, "SKILL", "Skill 1")} style={{ background: "#3b82f6", padding: "8px 12px" }}>S1</Button>
              <Button onClick={() => triggerManualAction(2, "SKILL", "Skill 2")} style={{ background: "#8b5cf6", padding: "8px 12px" }}>S2</Button>
              <Button onClick={() => triggerManualAction(2, "SKILL", "Ultimate")} style={{ background: "#ef4444", padding: "8px 12px" }}>Ult</Button>
              <Button onClick={() => triggerManualAction(2, "SPELL", hero2Spell)} style={{ background: "#10b981", padding: "8px 12px" }}>Spell</Button>
              {(gameState.hero2?.items || []).filter(it => it?.active).map((it) => (
                <Button
                  key={`h2-${it.name}`}
                  onClick={() => triggerManualAction(2, "ITEM_ACTIVE", it.name)}
                  style={{ background: "#f97316", padding: "8px 12px" }}
                >
                  {it.name}
                </Button>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Battle Arena */}
      {gameState && (
        <Card neon style={{ padding: 20 }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16
          }}>
            <div style={{ color: "#9ca3af", display: "flex", alignItems: "center", gap: 8 }}>
              <Clock size={16} />
              Time: <span style={{ color: "#fff", fontFamily: "monospace" }}>{gameState.timeSeconds.toFixed(1)}s</span>
            </div>

            {gameState.isFinished && (
              <div style={{
                padding: "8px 20px",
                borderRadius: 8,
                background: "linear-gradient(135deg, #fbbf24, #f59e0b)",
                color: "#000",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}>
                <Award size={18} />
                WINNER: {gameState.winner}
              </div>
            )}

            {isRunning && !gameState.isFinished && (
              <div style={{ color: "#22c55e", fontWeight: "bold", display: "flex", alignItems: "center", gap: 8 }}>
                <Activity size={16} className="animate-pulse" />
                FIGHTING...
              </div>
            )}
          </div>

          {/* HP Bars */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div>
              <div style={{ color: "#3b82f6", fontWeight: "bold", marginBottom: 8 }}>
                {gameState.hero1.heroName}
              </div>
              <HPBar
                current={gameState.hero1.currentHP}
                max={gameState.hero1.maxHP}
                label="HP"
                color="#3b82f6"
              />
              {gameState.hero1.maxMana > 0 && (
                <div style={{ marginTop: 8 }}>
                  <HPBar
                    current={gameState.hero1.currentMana}
                    max={gameState.hero1.maxMana}
                    label="Mana"
                    color="#8b5cf6"
                  />
                </div>
              )}
            </div>

            <div>
              <div style={{ color: "#ef4444", fontWeight: "bold", marginBottom: 8 }}>
                {gameState.hero2.heroName}
              </div>
              <HPBar
                current={gameState.hero2.currentHP}
                max={gameState.hero2.maxHP}
                label="HP"
                color="#ef4444"
              />
              {gameState.hero2.maxMana > 0 && (
                <div style={{ marginTop: 8 }}>
                  <HPBar
                    current={gameState.hero2.currentMana}
                    max={gameState.hero2.maxMana}
                    label="Mana"
                    color="#8b5cf6"
                  />
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Combo Builders */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <ComboBuilder
          combo={hero1Combo}
          setCombo={setHero1Combo}
          skills={hero1Skills}
          heroName={hero1Name}
        />
        <ComboBuilder
          combo={hero2Combo}
          setCombo={setHero2Combo}
          skills={hero2Skills}
          heroName={hero2Name}
        />
      </div>

      {/* Arcana & Spells Pickers */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <ArcanaPickerCompact
            arcanas={arcanas}
            arcanaSet={hero1Arcana}
            setArcanaSet={setHero1Arcana}
            heroName={hero1Name}
            accentColor="#3b82f6"
          />
          <SpellPickerCompact
            selected={hero1Spell}
            onChange={setHero1Spell}
            heroName={hero1Name}
            accentColor="#3b82f6"
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <ArcanaPickerCompact
            arcanas={arcanas}
            arcanaSet={hero2Arcana}
            setArcanaSet={setHero2Arcana}
            heroName={hero2Name}
            accentColor="#ef4444"
          />
          <SpellPickerCompact
            selected={hero2Spell}
            onChange={setHero2Spell}
            heroName={hero2Name}
            accentColor="#ef4444"
          />
        </div>
      </div>

      {/* Damage Preview Panel (New Feature) */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <DamagePreviewPanel
          title="Hero 1 Damage Estimate"
          heroName={hero1Name}
          damageData={damagePreview1}
        />
        <DamagePreviewPanel
          title="Hero 2 Damage Estimate"
          heroName={hero2Name}
          damageData={damagePreview2}
        />
      </div>

      {/* Item Shops */}
      {ItemShopPicker && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <ItemShopPicker
            items={items}
            selected={hero1Items}
            setSelected={setHero1Items}
            title={`${hero1Name} Items`}
            compact
          />
          <ItemShopPicker
            items={items}
            selected={hero2Items}
            setSelected={setHero2Items}
            title={`${hero2Name} Items`}
            compact
          />
        </div>
      )}

      {/* Combat Log */}
      {gameState && (
        <CombatLog events={gameState.combatLog?.events || []} maxHeight={400} />
      )}

      {/* Statistics */}
      {simStats && (
        <div>
          <div style={{
            fontWeight: "bold",
            color: "#ffd7dc",
            marginBottom: 12,
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 18
          }}>
            <TrendingUp size={20} />
            BATTLE STATISTICS
          </div>
          <StatisticsPanel stats={simStats} />
        </div>
      )}
    </div>
  );
}
